import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Content, Title } from 'components';
import { encrypt } from 'utils/Crypto';
import CONSTANTS from 'constants';
/*
 *----------------------------------------
 * [Component] 쇼핑몰 > 메인 > 카테고리 목록 컴포넌트
 * ----------------------------------------
 */
const CategoryList = ({
  screenSize,
  categories = [],
  isPresent = false,
  userInfo = null,
}) => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [leftOpacity, setLeftOpacity] = useState(0); // 왼쪽 그라데이션 투명도
  const [rightOpacity, setRightOpacity] = useState(1); // 오른쪽 그라데이션 투명도
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const checkScroll = () => {
      if (containerRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = containerRef.current;

        const isScrollable = scrollWidth > clientWidth;
        setHasScroll(isScrollable);

        // 왼쪽 & 오른쪽 스크롤 여부 감지하여 opacity 조절
        setLeftOpacity(scrollLeft === 0 ? 0 : 1);
        setRightOpacity(scrollLeft + clientWidth >= scrollWidth ? 0 : 1);
      }
    };

    checkScroll(); // 최초 실행

    containerRef.current?.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      containerRef.current?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [categories, screenSize]);

  const CategoryList = React.useMemo(() => {
    if (!categories?.length) return [];

    if (userInfo && userInfo?.business_level === 2) {
      return categories;
    } else {
      return categories?.filter(
        (category) =>
          category?.product_category_name !== CONSTANTS.BUSINESS_CATEGORY_NAME
      );
    }
  }, [categories, userInfo]);

  return (
    <Content
      maxWidth={1500}
      padding={0}
      style={{
        margin: screenSize >= 768 ? '135px auto' : '50px auto',
        position: 'relative',
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      {/* 좌측 그라데이션 */}
      <div
        style={{
          position: 'absolute',
          width: 50,
          height: '100%',
          top: 0,
          left: -10,
          background:
            'linear-gradient(to right, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0) 100%)',
          zIndex: 3,
          opacity: leftOpacity,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: 'none',
        }}
      ></div>

      {/* 우측 그라데이션 */}
      <div
        style={{
          position: 'absolute',
          width: 50,
          height: '100%',
          top: 0,
          right: -10,
          background:
            'linear-gradient(to left, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0) 100%)',
          zIndex: 3,
          opacity: rightOpacity,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: 'none',
        }}
      ></div>

      <div
        ref={containerRef}
        className="custom-scroll-hidden"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: hasScroll ? 'flex-start' : 'center',
          alignItems: 'flex-start',
          overflowX: 'auto',
          padding: '12px 0',
          cursor: 'pointer',
          width: '100%',
          gap: screenSize >= 768 ? 42 : 10,
          minWidth: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        {CategoryList?.map((item, index) => (
          <div
            className="category_hover"
            key={`category_main_${index}`}
            style={{ textAlign: 'center' }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={() =>
              navigate(
                isPresent
                  ? `/products?type=present&category=${encodeURIComponent(
                      encrypt(item.product_category_id)
                    )}`
                  : `/products?category=${encodeURIComponent(
                      encrypt(item.product_category_id)
                    )}`
              )
            }
          >
            {/* 이미지 */}
            <div
              style={{
                width: screenSize >= 768 ? 100 : 64,
                height: screenSize >= 768 ? 100 : 64,
                borderRadius: 25,
                background: '#F7F7F8',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'box-shadow 0.3s ease-in-out',
                boxShadow:
                  hoveredIndex === index
                    ? '0 4px 10px rgba(0, 0, 0, 0.1)'
                    : 'none',
              }}
            >
              <img
                src={
                  item.icon_url
                    ? `${process.env.REACT_APP_STORE_URL}/${item.icon_url}`
                    : '/cropped-JM-COFFEE_99_favicon-32x32.png'
                }
                alt=""
                style={{
                  maxWidth: screenSize >= 768 ? 50 : 30,
                  maxHeight: screenSize >= 768 ? 50 : 30,
                  width: '100%',
                  height: '100%',
                  opacity: item.icon_url ? 1 : 0.1,
                }}
              />
            </div>
            {/* 텍스트 */}
            <Title
              size={5}
              align="center"
              style={{
                lineHeight: '1.25',
                marginTop: 12,
                wordBreak: 'keep-all',
                whiteSpace: 'normal',
                fontSize: screenSize >= 768 ? 16 : 13,
                fontFamily: 'Pretendard',
                color:
                  hoveredIndex === index
                    ? 'var(--red-secondary-color)'
                    : 'var(--black-color)',
                transition: 'color 0.3s ease-in-out',
              }}
            >
              {item.product_category_name}
            </Title>
          </div>
        ))}
      </div>
    </Content>
  );
};

export default CategoryList;
